/** Konsultasi platform threads, messages, and investigation access. */

import { createDoc, updateDoc, getById } from '../service';
import { COL } from '../../collections';
import { utcNowIso } from '../../format';
import { notifyRoleUser } from './notify';
import type {
  ConsultDoc,
  ConsultFileRef,
  ConsultKind,
  ConsultMention,
  ConsultMessageDoc,
  ConsultStatus,
} from '../../types';

/** Create a consult thread (konsultasi topic, audience request, or dosen saran). */
export async function addConsult(input: {
  skripsiId: string;
  mahasiswaId: string;
  dosenId: string;
  kind: Exclude<ConsultKind, 'izin_sempro' | 'izin_sidang'>;
  topic: string;
  note?: string;
  dateTime?: string;
  actorId: string;
  actorName?: string;
}) {
  const now = utcNowIso();
  const doc = await createDoc<ConsultDoc>(COL.consults, {
    skripsiId: input.skripsiId,
    mahasiswaId: input.mahasiswaId,
    dosenId: input.dosenId,
    kind: input.kind,
    topic: input.topic,
    note: input.note,
    dateTime: input.dateTime,
    status: 'menunggu',
    log: [
      {
        by: input.actorId,
        byName: input.actorName,
        at: now,
        action:
          input.kind === 'audience'
            ? 'mengajukan audiensi'
            : input.kind === 'saran'
              ? 'memberi saran audiensi'
              : 'membuka konsultasi',
        note: input.topic,
      },
    ],
    createdAt: now,
    updatedAt: now,
  });
  // Notify the other party — a new thread is invisible without a push because
  // consult lists are the only other discovery path.
  const peerIsMahasiswa = input.actorId !== input.mahasiswaId;
  await notifyRoleUser(
    peerIsMahasiswa ? input.mahasiswaId : input.dosenId,
    peerIsMahasiswa ? 'mahasiswa' : 'dosen',
    {
      title:
        input.kind === 'audience'
          ? 'Permintaan audiensi baru'
          : input.kind === 'saran'
            ? 'Saran audiensi baru'
            : 'Konsultasi baru',
      body: `${input.actorName ?? 'Rekan'} membuka: ${input.topic}`,
      type: 'info',
      link: peerIsMahasiswa ? 'bimbingan' : 'konsultasi',
    },
  );
  return doc;
}

/** Ask a pembimbing for permission (izin) to proceed to sempro/sidang/penelitian. */
export async function requestPembimbingIzin(input: {
  skripsiId: string;
  mahasiswaId: string;
  dosenId: string;
  kind: 'izin_sempro' | 'izin_sidang' | 'izin_penelitian';
  note?: string;
  actorId: string;
  actorName?: string;
}) {
  const now = utcNowIso();
  const topic =
    input.kind === 'izin_sempro'
      ? 'Permohonan izin seminar proposal'
      : input.kind === 'izin_sidang'
        ? 'Permohonan izin ujian skripsi'
        : 'Permohonan izin penelitian';
  const doc = await createDoc<ConsultDoc>(COL.consults, {
    skripsiId: input.skripsiId,
    mahasiswaId: input.mahasiswaId,
    dosenId: input.dosenId,
    kind: input.kind,
    topic,
    note: input.note,
    status: 'menunggu',
    log: [
      {
        by: input.actorId,
        byName: input.actorName,
        at: now,
        action: 'mengajukan izin',
        note: input.note,
      },
    ],
    createdAt: now,
    updatedAt: now,
  });
  await notifyRoleUser(input.dosenId, 'dosen', {
    title: 'Permohonan izin baru',
    body: `${input.actorName ?? 'Mahasiswa'} mengajukan: ${topic}`,
    type: 'info',
    link: 'konsultasi',
  });
  return doc;
}

/** Approve/reject a consult (audience, saran, izin). */
export async function respondConsult(input: {
  id: string;
  status: ConsultStatus;
  response?: string;
  actorId: string;
  actorName?: string;
}) {
  const cur = await getById<ConsultDoc>(COL.consults, input.id);
  const log = [...(cur?.log ?? [])];
  log.push({
    by: input.actorId,
    byName: input.actorName,
    at: utcNowIso(),
    action: `menindaklanjuti menjadi ${input.status}`,
    note: input.response,
  });
  await updateDoc<ConsultDoc>(COL.consults, input.id, {
    status: input.status,
    response: input.response,
    log,
    updatedAt: utcNowIso(),
  });
  if (cur) {
    const peerIsMahasiswa = input.actorId !== cur.mahasiswaId;
    await notifyRoleUser(
      peerIsMahasiswa ? cur.mahasiswaId : cur.dosenId,
      peerIsMahasiswa ? 'mahasiswa' : 'dosen',
      {
        title:
          input.status === 'disetujui'
            ? 'Konsultasi disetujui'
            : input.status === 'ditolak'
              ? 'Konsultasi ditolak'
              : 'Konsultasi diperbarui',
        body: `${input.actorName ?? 'Rekan'} menindaklanjuti: ${cur.topic}`,
        type: input.status === 'ditolak' ? 'error' : 'success',
        link: peerIsMahasiswa ? 'bimbingan' : 'konsultasi',
      },
    );
  }
}

/** Settle an agreed appointment date/time on an audience/saran consult. */
export async function scheduleConsult(input: {
  id: string;
  scheduledAt: string;
  actorId: string;
  actorName?: string;
}) {
  const cur = await getById<ConsultDoc>(COL.consults, input.id);
  const log = [...(cur?.log ?? [])];
  log.push({
    by: input.actorId,
    byName: input.actorName,
    at: utcNowIso(),
    action: 'menetapkan jadwal pertemuan',
    note: input.scheduledAt,
  });
  await updateDoc<ConsultDoc>(COL.consults, input.id, {
    scheduledAt: input.scheduledAt,
    status: 'disetujui',
    log,
    updatedAt: utcNowIso(),
  });
  if (cur) {
    const peerIsMahasiswa = input.actorId !== cur.mahasiswaId;
    await notifyRoleUser(
      peerIsMahasiswa ? cur.mahasiswaId : cur.dosenId,
      peerIsMahasiswa ? 'mahasiswa' : 'dosen',
      {
        title: 'Jadwal pertemuan ditetapkan',
        body: `${input.actorName ?? 'Rekan'} menetapkan jadwal untuk: ${cur.topic}`,
        type: 'success',
        link: peerIsMahasiswa ? 'bimbingan' : 'konsultasi',
      },
    );
  }
}

/** Kaprodi grants read access to a private consult for investigation (audited). */
export async function grantConsultInvestigation(input: {
  id: string;
  actorId: string;
  actorName?: string;
  reason?: string;
}) {
  const cur = await getById<ConsultDoc>(COL.consults, input.id);
  const investigation = [
    ...(cur?.investigation ?? []),
    { by: input.actorId, byName: input.actorName, at: utcNowIso(), reason: input.reason },
  ];
  const log = [...(cur?.log ?? [])];
  log.push({
    by: input.actorId,
    byName: input.actorName,
    at: utcNowIso(),
    action: 'membuka akses investigasi',
    note: input.reason,
  });
  return updateDoc<ConsultDoc>(COL.consults, input.id, {
    investigation,
    log,
    updatedAt: utcNowIso(),
  });
}

/** Append a generic activity log entry (e.g. reminder, follow-up). */
export async function addConsultLog(input: {
  id: string;
  action: string;
  note?: string;
  actorId: string;
  actorName?: string;
}) {
  const cur = await getById<ConsultDoc>(COL.consults, input.id);
  const log = [...(cur?.log ?? [])];
  log.push({
    by: input.actorId,
    byName: input.actorName,
    at: utcNowIso(),
    action: input.action,
    note: input.note,
  });
  return updateDoc<ConsultDoc>(COL.consults, input.id, { log, updatedAt: utcNowIso() });
}

/** Post a chat message on a konsultasi thread (markdown + mentions + files). */
export async function addConsultMessage(input: {
  consultId: string;
  authorId: string;
  authorName?: string;
  text: string;
  mentions?: ConsultMention[];
  files?: ConsultFileRef[];
}) {
  const consult = await getById<ConsultDoc>(COL.consults, input.consultId);
  if (!consult) throw new Error('Konsultasi tidak ditemukan');
  const doc = await createDoc<ConsultMessageDoc>(COL.consultMessages, {
    consultId: input.consultId,
    authorId: input.authorId,
    authorName: input.authorName,
    text: input.text,
    mentions: input.mentions,
    files: input.files,
    readBy: [input.authorId],
  });
  // Bump the thread so it re-sorts to the top of every inbox.
  await updateDoc<ConsultDoc>(COL.consults, input.consultId, { updatedAt: doc.updatedAt });
  // Notify the peer if they are NOT the one posting.
  const peerId = input.authorId === consult.mahasiswaId ? consult.dosenId : consult.mahasiswaId;
  if (peerId && peerId !== input.authorId) {
    const preview = (input.text ?? '').replace(/\s+/g, ' ').slice(0, 90) || 'Membagikan berkas';
    const peerIsMahasiswa = peerId === consult.mahasiswaId;
    await notifyRoleUser(peerId, peerIsMahasiswa ? 'mahasiswa' : 'dosen', {
      title: `Pesan baru dari ${input.authorName ?? 'rekan'}`,
      body: preview,
      type: 'info',
      link: peerIsMahasiswa ? 'bimbingan' : 'konsultasi',
    });
  }
  return doc;
}

/** Mark a consult message as read by a viewer (idempotent). */
export async function markConsultMessageRead(input: {
  consultId: string;
  messageId: string;
  readerId: string;
}) {
  const cur = await getById<ConsultMessageDoc>(COL.consultMessages, input.messageId);
  const readBy = [...(cur?.readBy ?? [])];
  if (!readBy.includes(input.readerId)) {
    readBy.push(input.readerId);
    await updateDoc<ConsultMessageDoc>(COL.consultMessages, input.messageId, { readBy });
  }
}
